import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { authService } from '../../services/auth';

type SignInMode = 'select' | 'email' | 'reset';

/**
 * SignInOptions
 *
 * Reusable, self-contained sign-in UI offering:
 *  - Google sign-in
 *  - Email + password sign-in
 *  - Email + password account creation
 *  - Password reset
 *
 * Used by the main sign-in screen (`App.ts`) and the invite landing page
 * (`InviteRedemption.ts`). Authentication state is observed globally via
 * `authService.onAuthStateChanged` / `appState`, so callers do not need to
 * handle the success case here — the surrounding app re-renders on auth.
 */
@customElement('sign-in-options')
export class SignInOptions extends LitElement {
  createRenderRoot() {
    return this;
  }

  /** Optional description shown above the buttons in 'select' mode. */
  @property({ type: String })
  description = '';

  /** Optional label override for the Google sign-in button. */
  @property({ type: String, attribute: 'google-label' })
  googleLabel = 'Sign in with Google';

  @state()
  private mode: SignInMode = 'select';

  @state()
  private emailInput = '';

  @state()
  private passwordInput = '';

  @state()
  private loading = false;

  @state()
  private errorMessage: string | null = null;

  @state()
  private resetEmailSent = false;

  render() {
    return html`
      <div class="w-full">
        ${this.mode === 'select' ? this.renderSelect() : nothing}
        ${this.mode === 'email' ? this.renderEmailSignIn() : nothing}
        ${this.mode === 'reset' ? this.renderPasswordReset() : nothing}
      </div>
    `;
  }

  private renderSelect() {
    return html`
      <div class="w-full space-y-3">
        ${this.description
          ? html`<p class="text-base-content/70 text-sm mb-4">${this.description}</p>`
          : nothing}

        <button
          class="btn btn-primary btn-block gap-3 shadow-lg hover:shadow-xl transition-shadow"
          @click=${this.handleGoogleSignIn}
          ?disabled=${this.loading}
          aria-label="Sign in with Google"
        >
          <svg class="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          ${this.googleLabel}
        </button>

        <div class="divider text-xs text-base-content/50">or</div>

        <button
          class="btn btn-outline btn-block gap-3"
          @click=${() => this.switchMode('email')}
          ?disabled=${this.loading}
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          Sign in with email
        </button>

        <p class="text-xs text-base-content/60 text-center pt-1">
          New here? Just sign in — we'll set up your account automatically.
        </p>
      </div>
    `;
  }

  private renderBackButton(target: SignInMode) {
    return html`
      <button
        type="button"
        class="btn btn-ghost btn-sm gap-1 -ml-2"
        @click=${() => this.switchMode(target)}
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
        Back
      </button>
    `;
  }

  private renderErrorAlert() {
    if (!this.errorMessage) return nothing;
    return html`
      <div class="alert alert-error text-sm py-2" role="alert">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <span>${this.errorMessage}</span>
      </div>
    `;
  }

  private renderEmailSignIn() {
    return html`
      <form class="w-full space-y-4" @submit=${this.handleEmailSignIn}>
        ${this.renderBackButton('select')}
        ${this.renderErrorAlert()}

        <div class="form-control">
          <label class="sr-only" for="signin-email">Email</label>
          <input
            id="signin-email"
            type="email"
            placeholder="Email"
            autocomplete="email"
            class="input input-bordered w-full"
            .value=${this.emailInput}
            @input=${(e: Event) => this.emailInput = (e.target as HTMLInputElement).value}
            required
          />
        </div>

        <div class="form-control">
          <label class="sr-only" for="signin-password">Password</label>
          <input
            id="signin-password"
            type="password"
            placeholder="Password (min 6 characters)"
            autocomplete="current-password"
            class="input input-bordered w-full"
            .value=${this.passwordInput}
            @input=${(e: Event) => this.passwordInput = (e.target as HTMLInputElement).value}
            minlength="6"
            required
          />
        </div>

        <button
          type="submit"
          class="btn btn-primary btn-block"
          ?disabled=${this.loading}
        >
          ${this.loading ? html`<span class="loading loading-spinner loading-sm"></span>` : 'Continue'}
        </button>

        <p class="text-xs text-base-content/50 text-center">
          We'll sign you in. If this is your first time, an account will be created.
        </p>

        <div class="flex justify-center text-sm">
          <button
            type="button"
            class="link link-hover text-base-content/60"
            @click=${() => this.switchMode('reset')}
          >
            Forgot password?
          </button>
        </div>
      </form>
    `;
  }

  private renderPasswordReset() {
    return html`
      <div class="w-full space-y-4">
        ${this.renderBackButton('email')}

        <h3 class="text-lg font-semibold">Reset Password</h3>

        ${this.resetEmailSent
          ? html`
              <div class="alert alert-success text-sm" role="status">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Check your email for reset instructions</span>
              </div>
            `
          : html`
              <form @submit=${this.handlePasswordReset}>
                ${this.errorMessage
                  ? html`
                      <div class="alert alert-error text-sm py-2 mb-4" role="alert">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <span>${this.errorMessage}</span>
                      </div>
                    `
                  : nothing}

                <p class="text-sm text-base-content/70 mb-4">
                  Enter your email and we'll send you a link to reset your password.
                </p>

                <div class="form-control mb-4">
                  <label class="sr-only" for="reset-email">Email</label>
                  <input
                    id="reset-email"
                    type="email"
                    placeholder="Email"
                    autocomplete="email"
                    class="input input-bordered w-full"
                    .value=${this.emailInput}
                    @input=${(e: Event) => this.emailInput = (e.target as HTMLInputElement).value}
                    required
                  />
                </div>

                <button
                  type="submit"
                  class="btn btn-primary btn-block"
                  ?disabled=${this.loading}
                >
                  ${this.loading ? html`<span class="loading loading-spinner loading-sm"></span>` : 'Send Reset Link'}
                </button>
              </form>
            `}
      </div>
    `;
  }

  private switchMode(target: SignInMode) {
    this.mode = target;
    this.errorMessage = null;
    if (target === 'select') {
      this.emailInput = '';
      this.passwordInput = '';
      this.resetEmailSent = false;
    }
    if (target === 'reset') {
      this.resetEmailSent = false;
    }
  }

  private async handleGoogleSignIn() {
    if (this.loading) return;
    this.loading = true;
    this.errorMessage = null;
    try {
      await authService.signInWithGoogle();
      // Auth state listener will propagate success to the rest of the app.
    } finally {
      this.loading = false;
    }
  }

  private async handleEmailSignIn(e: Event) {
    e.preventDefault();
    if (this.loading) return;

    this.loading = true;
    this.errorMessage = null;

    // Unified flow: sign in if the account exists, create it if not.
    const result = await authService.signInOrSignUpWithEmail(this.emailInput, this.passwordInput);

    this.loading = false;
    if (result.error) {
      this.errorMessage = result.error;
      return;
    }
    this.resetForm();
  }

  private async handlePasswordReset(e: Event) {
    e.preventDefault();
    if (this.loading) return;

    this.loading = true;
    this.errorMessage = null;

    const result = await authService.resetPassword(this.emailInput);

    this.loading = false;
    if (result.error) {
      this.errorMessage = result.error;
      return;
    }
    this.resetEmailSent = true;
  }

  private resetForm() {
    this.emailInput = '';
    this.passwordInput = '';
    this.errorMessage = null;
    this.mode = 'select';
    this.resetEmailSent = false;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sign-in-options': SignInOptions;
  }
}
